import React from 'react'
import { PlanSeq } from '@mobi/api-types'
import { dayjs } from '@mobi/utils/date'
import { CountdownBadge } from '@mobi/component-library/Feedback/CountdownBadge'
import { RadioButton } from '@mobi/component-library/Selection&Input/RadioButton'
import { StatusBadge } from '@mobi/component-library/Feedback/StatusBadge'
import type { BetLoopShareTicket } from '@core/Areas/BetLoop/types'
import { TicketDisplayContext } from '@core/Areas/AccountActivity/context'
import type { Ticket, TicketStatus } from '@core/Areas/AccountActivity/types'
import {
  getStatusText,
  getTicketLayoutType,
  isTippingTicket,
  TicketLayoutType,
} from '@core/Areas/AccountActivity/Components/Ticket/helpers'

export const CountdownStatus: React.FC<{
  ticket: Ticket | BetLoopShareTicket
  status: TicketStatus
}> = ({ ticket, status }) => {
  const ticketContext = React.useContext(TicketDisplayContext)

  const isContextShareType =
    ticketContext.type === 'share' && ticketContext.data.forcedStatus !== 'resulted'

  const ticketLayoutType = getTicketLayoutType(ticket)

  const isPendingTab = status === 'Pending'
  const isTipping = isTippingTicket(ticket)
  const isFavouriteNumbers = ticket.planSeq === PlanSeq.FavouriteNumbers

  const countdownDate =
    (isPendingTab || (isContextShareType && status !== 'Closed')) &&
    !isFavouriteNumbers &&
    !isTipping &&
    getCountdownDate(ticket, ticketLayoutType)

  if (isContextShareType && status !== 'Closed') {
    const isMulti = ticketLayoutType === 'multi'
    const isInBetLoopFeed = ticketContext.data.display === 'bet-loop'
    const isInBetLoopQuickShare = ticketContext.data.display === 'betloop-quick-share'

    if (isInBetLoopFeed && ticketContext.data.forcedStatus === 'pending') {
      return (
        <StatusBadge color='neutral' badgeSize='S'>
          Pending
        </StatusBadge>
      )
    }

    return (
      <>
        {countdownDate ? (
          isInBetLoopFeed || isInBetLoopQuickShare ? (
            <CountdownBadge badgeSize='S' advertisedStartTime={new Date(countdownDate)} />
          ) : (
            !isMulti && (
              <StatusBadge color='gray' badgeSize='S'>
                {dayjs(countdownDate).format('D MMM | H:mm')}
              </StatusBadge>
            )
          )
        ) : null}

        {ticketContext.data.display === 'betloop-quick-share' && (
          <RadioButton
            type='Tick'
            id={ticket.timestamp}
            isChecked={!!ticketContext.data.isSelected}
            size='md'
          />
        )}
      </>
    )
  }

  return (
    <>
      {countdownDate && (
        <CountdownBadge badgeSize='S' advertisedStartTime={new Date(countdownDate)} />
      )}

      {status !== 'Pending' && (
        <StatusBadge badgeSize='S' color={status === 'Won' ? 'success' : 'neutral'}>
          {getStatusText(status)}
        </StatusBadge>
      )}
    </>
  )
}

// =======
// Helpers
// =======

function getCountdownDate(
  ticket: Ticket | BetLoopShareTicket,
  ticketLayoutType: TicketLayoutType
): string {
  if (ticketLayoutType === 'multi') {
    const currentTime = dayjs()
    const timesFromNow = ticket.legs.map(leg => {
      const diff = currentTime.diff(leg.advertisedStartTime)
      // Allow time if within 2min
      if (diff < 1000 * 60 * 2) {
        return diff
      }
      return -Infinity
    })

    const closestTime = Math.max(...timesFromNow)
    const indexOfClosest = timesFromNow.findIndex(time => time === closestTime)

    if (indexOfClosest === -1) {
      return ticket.legs[0].advertisedStartTime
    }

    return ticket.legs[indexOfClosest].advertisedStartTime
  }

  if (ticketLayoutType === 'single-multiple' && ticket.allUpStartTimes) {
    const currentTime = dayjs()
    const timesFromNow = ticket.allUpStartTimes.map(time => {
      const diff = currentTime.diff(dayjs(time))
      // Allow time if within 2min
      if (diff < 1000 * 60 * 2) {
        return diff
      }
      return -Infinity
    })
    const closestTime = Math.max(...timesFromNow)
    const indexOfClosest = timesFromNow.findIndex(time => time === closestTime)
    if (indexOfClosest === -1) {
      return dayjs(ticket.allUpStartTimes[0]).toISOString()
    }
    return dayjs(ticket.allUpStartTimes[indexOfClosest]).toISOString()
  }

  return ticket.legs[0].advertisedStartTime
}
